import React, { useCallback, useEffect, useState } from "react";
import { mergeRight } from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, pick, set } from "ramda";
import Grid from "@mui/material/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { notBlank } from "../util/ext";
import { useAuthenticationCreate } from "./authenticationService";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    organizationId: "",
    proxyPrefix: "",
  },
};

const AddEzProxyAuthenticationDialog = ({
  authentication,
  organizationId,
  open,
  onClose,
  onSave,
}) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (authentication && authentication.authenticationId) {
      setFields(pick(["organizationId", "proxyPrefix"], authentication));
    } else {
      setFields(
        mergeRight(initialState.fields, {
          organizationId: organizationId,
        }),
      );
    }
  }, [authentication, organizationId]);

  const onFieldChanged = field => value => {
    const val = value.target ? value.target.value : value;
    const update = set(lensProp(field), val, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const authenticationCreate = useAuthenticationCreate();
  const createAuthentication = async authentication => {
    if (
      notBlank(authentication.organizationId) &&
      notBlank(authentication.proxyPrefix)
    ) {
      const createBody = pick(["proxyPrefix"], authentication);
      await authenticationCreate.mutateAsync({
        resourceIds: {
          organizationId: authentication.organizationId,
          type: "ezproxy",
        },
        data: createBody,
      });
    } else {
      throw new Error("Both Organization and Proxy Prefix are required.");
    }
  };

  const handleError = (message, err) => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedAuthentication = {
      organizationId: fields.organizationId,
      proxyPrefix: fields.proxyPrefix,
    };

    try {
      await createAuthentication(mergedAuthentication);
      onSave(mergedAuthentication);
    } catch (err) {
      handleError("Unable to save EzProxy Authentication.", err);
    }
  };

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} aria-labelledby="form-dialog-title">
      {notBlank(authentication) && notBlank(authentication.authenticationId) ? (
        <DialogTitle id="form-dialog-title">
          Authentication ({authentication.authenticationId})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">
          New EzProxy Authentication
        </DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>
          Enter EzProxy Authentication Information.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <OrganizationAutoComplete
              autoFocus
              value={fields.organizationId}
              label="Organization"
              onChange={onFieldChanged("organizationId")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="proxyPrefix"
              label="Proxy Prefix"
              fullWidth
              value={fields.proxyPrefix || ""}
              onChange={onFieldChanged("proxyPrefix")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEzProxyAuthenticationDialog;
